import React, { forwardRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    ListItemButton,
    ListItemAvatar,
    Avatar,
    Divider,
    Typography,
    Box,
} from '@mui/material';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ExpandLess from '@mui/icons-material/ExpandLess';

/**
 * AppList - A generic list component that supports various item types and nested structures
 * @param {Object} props
 * @param {Array} props.items - Array of list items
 * @param {string} [props.variant='simple'] - List variant (simple, button, icon, avatar, nested)
 * @param {boolean} [props.dense=false] - Whether to render a dense list
 * @param {boolean} [props.dividers=false] - Whether to show dividers between items
 * @param {Function} [props.onItemClick] - Callback when an item is clicked
 * @param {number} [props.level=0] - Nesting level (for indentation)
 * @param {Object} [props.sx] - Additional styles for the list
 */
const AppList = forwardRef(({
    items,
    variant = 'simple',
    dense = false,
    dividers = false,
    onItemClick,
    level = 0,
    sx = {},
    ...props
}, ref) => {
    const [localOpenItems, setLocalOpenItems] = useState({});
    const isOpen = (label) =>  localOpenItems[label] || false;

    const handleToggle = (label) => {
        setLocalOpenItems(prev => ({
            ...prev,
            [label]: !localOpenItems[label]
        }));
    };

    const listStyles = {
        pl: level * 2,
        ...sx
    };

    const renderListItemContent = (item) => {
        const commonProps = {
            primary: item.label,
            secondary: item.secondary,
            primaryTypographyProps: item.primaryTypographyProps,
            secondaryTypographyProps: item.secondaryTypographyProps,
        };

        const hasSubItems = Array.isArray(item.subItems) && item.subItems.length > 0;

        switch (variant) {

            case 'button':
                return (
                    <ListItemButton>
                        {item.icon && <ListItemIcon>{item.icon}</ListItemIcon>}
                        <ListItemText {...commonProps} />
                        {hasSubItems && (
                            isOpen(item.label) ? <ExpandLess /> : <ExpandMore />
                        )}
                    </ListItemButton>
                );

            case 'icon':
                return (
                    <>
                        {item.icon && <ListItemIcon>{item.icon}</ListItemIcon>}
                        <ListItemText {...commonProps} />
                    </>
                );

            case 'avatar':
                return (
                    <>
                        <ListItemAvatar>
                            <Avatar src={item.avatar} alt={commonProps.primary}>
                                {!item.avatar && commonProps.primary?.charAt(0)}
                            </Avatar>
                        </ListItemAvatar>
                        <ListItemText {...commonProps} />
                    </>
                );

            default:
                return <ListItemText {...commonProps} />;
        }
    };

    const renderListItem = (item, index) => {
        const stack = [{ items: [item], level: level }];  // Wrap single item in array
        const renderedItems = [];

        while (stack.length > 0) {
            const { items: currentItems, level: currentLevel } = stack.pop();

            currentItems.forEach((currentItem, idx) => {
                const hasSubItems = Array.isArray(currentItem.subItems) && currentItem.subItems.length > 0;

                renderedItems.push(
                    <>
                        <ListItem
                            dense={dense}
                            disabled={currentItem.disabled}
                            onClick={hasSubItems ?
                                () => handleToggle(currentItem.label) :
                                () => onItemClick?.(currentItem.label)
                            }
                            {...(variant === 'button' ? { disablePadding: true } : {})}
                            sx={{ pl: currentLevel * 2 }}
                        >
                            {renderListItemContent(currentItem)}
                        </ListItem>
                        {dividers && idx < currentItems.length - 1 && <Divider />}
                    </>
                );

                if (hasSubItems && isOpen(currentItem.label)) {
                    currentItem.subItems.slice().reverse().forEach((subItem) => {
                        stack.push({
                            items: [subItem],
                            level: currentLevel + 1
                        });
                    });
                }
            });
        }

        return renderedItems;
    };

    return items?.length ? (
        <List ref={ref} sx={listStyles} {...props}>
            {items.map((item, index) => renderListItem(item, index))}
        </List>
    ) : (
        <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography color="text.secondary">No items to display</Typography>
        </Box>
    );
});

AppList.propTypes = {
    items: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        primary: PropTypes.node,
        label: PropTypes.string,
        secondary: PropTypes.node,
        icon: PropTypes.node,
        avatar: PropTypes.string,
        disabled: PropTypes.bool,
        selected: PropTypes.bool,
        subItems: PropTypes.array,
        primaryTypographyProps: PropTypes.object,
        secondaryTypographyProps: PropTypes.object,
    })).isRequired,
    variant: PropTypes.oneOf(['simple', 'button', 'icon', 'avatar', 'nested']),
    dense: PropTypes.bool,
    dividers: PropTypes.bool,
    onItemClick: PropTypes.func,
    level: PropTypes.number,
    sx: PropTypes.object,
};

AppList.displayName = 'AppList';

export { AppList };